import { useState } from 'react';
import { useLanguage } from '../../../hooks/useLanguage';

interface NavbarNewProps {
  scrolled: boolean;
}

export default function NavbarNew({ scrolled }: NavbarNewProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { language, setLanguage } = useLanguage();

  const scrollToSection = (id: string) => {
    setMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navLabels = {
    de: ['Lösung', 'ROI', 'Kontakt'],
    en: ['Solution', 'ROI', 'Contact']
  };

  const ctaLabel = {
    de: 'Demo anfragen',
    en: 'Request Demo'
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'bg-black shadow-lg border-b border-white/10' : 'bg-transparent'
    }`}>
      <div className="mx-auto px-5 sm:px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/517d8e45247682e1e610f69e9004e6d7/27d85eeddc9e07e72840b82b740df7b1.svg"
              alt="TELIQone"
              className="h-7 w-auto"
            />
            <span className="text-white text-lg font-bold tracking-tight">
              TELIQ<span className="text-blue-500">one</span>
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {['solution', 'roi'].map((id, i) => (
              <button
                key={id}
                onClick={() => scrollToSection(id)}
                className="text-sm text-white/70 hover:text-white transition-colors whitespace-nowrap cursor-pointer"
              >
                {navLabels[language][i]}
              </button>
            ))}
            <a
              href="/contact"
              className="text-sm text-white/70 hover:text-white transition-colors whitespace-nowrap cursor-pointer"
            >
              {navLabels[language][2]}
            </a>
            
            {/* Language Switcher */}
            <div className="flex items-center gap-2 border border-white/20 rounded-md p-1">
              <button
                onClick={() => setLanguage('de')}
                className={`px-2 py-1 rounded transition-all cursor-pointer ${
                  language === 'de' ? 'bg-white/10' : 'hover:bg-white/5'
                }`}
                aria-label="Deutsch"
              >
                <span className="text-lg">🇩🇪</span>
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`px-2 py-1 rounded transition-all cursor-pointer ${
                  language === 'en' ? 'bg-white/10' : 'hover:bg-white/5'
                }`}
                aria-label="English"
              >
                <span className="text-lg">🇬🇧</span>
              </button>
            </div>

            <a
              href="mailto:info@teliq.one"
              className="px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-md hover:bg-blue-500 transition-all whitespace-nowrap cursor-pointer"
            >
              {ctaLabel[language]}
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden w-9 h-9 flex items-center justify-center text-white cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-black border-t border-white/10 px-5 py-4 flex flex-col gap-4">
          {['solution', 'roi'].map((id, i) => (
            <button
              key={id}
              onClick={() => scrollToSection(id)}
              className="text-sm text-white/70 hover:text-white transition-colors text-left whitespace-nowrap cursor-pointer"
            >
              {navLabels[language][i]}
            </button>
          ))}
          <a
            href="/contact"
            className="text-sm text-white/70 hover:text-white transition-colors text-left whitespace-nowrap cursor-pointer"
          >
            {navLabels[language][2]}
          </a>
          
          {/* Mobile Language Switcher */}
          <div className="flex items-center gap-2 border border-white/20 rounded-md p-1 w-fit">
            <button
              onClick={() => setLanguage('de')}
              className={`px-2 py-1 rounded transition-all cursor-pointer ${
                language === 'de' ? 'bg-white/10' : 'hover:bg-white/5'
              }`}
              aria-label="Deutsch"
            >
              <span className="text-lg">🇩🇪</span>
            </button>
            <button
              onClick={() => setLanguage('en')}
              className={`px-2 py-1 rounded transition-all cursor-pointer ${
                language === 'en' ? 'bg-white/10' : 'hover:bg-white/5'
              }`}
              aria-label="English"
            >
              <span className="text-lg">🇬🇧</span>
            </button>
          </div>

          <a
            href="mailto:info@teliq.one"
            className="px-5 py-3 bg-blue-600 text-white text-sm font-semibold rounded-md hover:bg-blue-500 transition-all text-center whitespace-nowrap cursor-pointer"
          >
            {ctaLabel[language]}
          </a>
        </div>
      )}
    </nav>
  );
}